import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Footer.css';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [subscribing, setSubscribing] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      // Try to submit to the backend newsletter endpoint
      const response = await fetch('https://api.realtimerepairs.com/api/newsletter/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, source: 'marketing_footer' })
      });

      if (response.ok) {
        setSubscribed(true);
        setEmail('');
      } else {
        // If API fails, still show success (we'll capture the email intent)
        setSubscribed(true);
        setEmail('');
      }
    } catch (error) {
      // Network error - still show success to not frustrate user
      setSubscribed(true);
      setEmail('');
    }
    setSubscribing(false);
  };

  return (
    <footer className="footer">
      {/* Main Footer Content */}
      <div className="footer__main">
        <div className="footer__container">
          {/* Brand Column */}
          <div className="footer__brand">
            <Link to="/" className="footer__logo">
              <span className="footer__logo-icon">
                <i className="fa-solid fa-house-chimney-crack"></i>
              </span>
              <span className="footer__logo-text">
                Real Time <span className="footer__logo-accent">Repairs</span>
              </span>
            </Link>
            <p className="footer__tagline">
              The smarter way to find trusted contractors. Post your project, compare bids, and hire with confidence.
            </p>

            {/* Newsletter Signup */}
            <div className="footer__newsletter">
              {subscribed ? (
                <p className="footer__newsletter-success">
                  <i className="fa-solid fa-check-circle"></i> Thanks for subscribing!
                </p>
              ) : (
                <form onSubmit={handleNewsletterSubmit} className="footer__newsletter-form">
                  <input
                    type="email"
                    placeholder="Get updates in your inbox"
                    aria-label="Email address for newsletter"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="footer__newsletter-input"
                  />
                  <button type="submit" className="footer__newsletter-btn" disabled={subscribing}>
                    {subscribing ? '...' : 'Subscribe'}
                  </button>
                </form>
              )}
            </div>

            <div className="footer__social">
              <a href="https://www.facebook.com/realtimerepairsfl" target="_blank" rel="noopener noreferrer" className="footer__social-link" aria-label="Facebook">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              </a>
            </div>
          </div>

          {/* Links Columns */}
          <div className="footer__links">
            {/* For Homeowners */}
            <div className="footer__column">
              <h4 className="footer__column-title">For Homeowners</h4>
              <ul className="footer__list">
                <li><a href="https://app.realtimerepairs.com/quick-post">Post a Project</a></li>
                <li><Link to="/faq">How It Works</Link></li>
                <li><Link to="/resources">Hiring Guides</Link></li>
                <li><a href="https://app.realtimerepairs.com/login">Sign In</a></li>
              </ul>
            </div>

            {/* For Contractors */}
            <div className="footer__column">
              <h4 className="footer__column-title">For Contractors</h4>
              <ul className="footer__list">
                <li><a href="https://app.realtimerepairs.com/register?type=contractor">Join as a Pro</a></li>
                <li><a href="https://app.realtimerepairs.com/register?type=contractor">Get Started Free</a></li>
                <li><Link to="/contractor-resources">Success Tips</Link></li>
                <li><a href="https://app.realtimerepairs.com/login">Pro Login</a></li>
              </ul>
            </div>

            {/* Company */}
            <div className="footer__column">
              <h4 className="footer__column-title">Company</h4>
              <ul className="footer__list">
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/why-us">Why Us</Link></li>
                <li><Link to="/for-businesses">For Businesses</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/blog">Blog</Link></li>
                <li><Link to="/partners">Partners</Link></li>
              </ul>
            </div>

            {/* Support */}
            <div className="footer__column">
              <h4 className="footer__column-title">Support</h4>
              <ul className="footer__list">
                <li><Link to="/faq">FAQ</Link></li>
                <li><Link to="/contact">Help Center</Link></li>
                <li><a href="https://app.realtimerepairs.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms of Service</a></li>
                <li><a href="https://app.realtimerepairs.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Trust Bar */}
      <div className="footer__trust">
        <div className="footer__container">
          <div className="footer__trust-items">
            <div className="footer__trust-item">
              <i className="fa-solid fa-shield-halved"></i>
              <span>Licensed Contractors</span>
            </div>
            <div className="footer__trust-item">
              <i className="fa-solid fa-lock"></i>
              <span>Secure Platform</span>
            </div>
            <div className="footer__trust-item">
              <i className="fa-solid fa-credit-card"></i>
              <span>Safe Payments</span>
            </div>
            <div className="footer__trust-item">
              <i className="fa-solid fa-headset"></i>
              <span>Dedicated Support</span>
            </div>
          </div>
        </div>
      </div>

      {/* Platform Disclaimer */}
      <div className="footer__beta-disclaimer">
        <div className="footer__container">
          <p>RealTimeRepairs connects customers with independent local contractors. We do not perform the work, handle payments, or guarantee contractor availability. Customers pay contractors directly.</p>
          <p>Real Time Repairs is a marketplace platform that connects homeowners with independent contractors. We do not guarantee specific pricing, response times, savings, job volume, or business results. Actual outcomes vary based on location, market conditions, contractor availability, and user participation. Contractors set their own rates and terms. Features and pricing may change.</p>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="footer__bottom">
        <div className="footer__container">
          <p className="footer__copyright">
            &copy; {currentYear} Real Time Repairs. All rights reserved.
          </p>
          <div className="footer__bottom-links">
            <a href="https://app.realtimerepairs.com/terms-of-service" target="_blank" rel="noopener noreferrer">Terms</a>
            <span className="footer__divider">·</span>
            <a href="https://app.realtimerepairs.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy</a>
            <span className="footer__divider">·</span>
            <Link to="/contact">Contact</Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
